import { cs, de, enCA, enUS, es, fr, frCA, it, nl } from 'date-fns/locale';
import {
  getBrowserCultureLang,
  getLanguageFromLocalStorage,
} from '@vendasta/galaxy/i18n/src/browser-locale/browser-locale';

const localeByLanguageCode: Record<string, Locale> = {
  en: enUS,
  'en-us': enUS,
  'en-ca': enCA,
  fr: fr,
  'fr-fr': fr,
  'fr-ca': frCA,
  cs: cs,
  nl: nl,
  de: de,
  'es-419': es,
  it: it,
  'it-it': it,
};

export const supportedLocales = Array.from(new Set(Object.values(localeByLanguageCode)));

/**
 * Based the user's preferred language from the navigator and finds the best locale match.
 * @returns {Locale} Returns a Locale that is the best match, enUs as the fallback.
 */
export function getLocale(): Locale {
  return getLocaleForLanguage(getLanguageFromLocalStorage() ?? getBrowserCultureLang() ?? '');
}

/**
 * Maps a language/locale code (BCP-47, e.g. 'fr-CA', 'en', 'es-419') to the matching
 * date-fns Locale object, enUS as the fallback. Use this instead of passing raw language
 * strings to APIs that require a date-fns Locale (e.g. DateAdapter#setLocale on a
 * DateFnsAdapter, which throws on string locales).
 * @param {string} languageCode the language/locale code to map
 * @returns {Locale} Returns the matching date-fns Locale, enUS as the fallback.
 */
export function getLocaleForLanguage(languageCode: string | null | undefined): Locale {
  const raw = (languageCode ?? '').toLowerCase();
  return localeByLanguageCode[raw] ?? localeByLanguageCode[raw.slice(0, 2)] ?? enUS;
}

/**
 * @param locale {string} A locale code for the locale format, typically from navigator.language (BCP-47)
 * @return { string } A locale code that is ISO 639-1 to deal missing matching locale codes between BCP-47 and ISO 639-1
 * (ISO 639-1 w/ optional two character region subtag) ex) en, en-US, en-CA, fr-CA, fr
 */
export const getLocaleCode = (locale?: string): string | undefined => {
  if (!locale) {
    return getLocale().code;
  }
  return locale;
};
